import { Content, Portal } from '@radix-ui/react-dropdown-menu'
import { cn } from '@seen/ui'
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type ReactNode,
} from 'react'

type ContentProps = ComponentPropsWithoutRef<typeof Content>

export interface DropdownMenuContentProps extends ContentProps {
  asChild?: boolean
  loop?: boolean
  onCloseAutoFocus?: ContentProps['onCloseAutoFocus']
  onEscapeKeyDown?: ContentProps['onEscapeKeyDown']
  onPointerDownOutside?: ContentProps['onPointerDownOutside']
  onFocusOutside?: ContentProps['onFocusOutside']
  onInteractOutside?: ContentProps['onInteractOutside']
  forceMount?: true
  side?: 'top' | 'right' | 'bottom' | 'left' // default bottom
  sideOffset?: number // default 0
  align?: 'start' | 'center' | 'end' // default center
  alignOffset?: number // default 0
  avoidCollisions?: boolean // default true
  collisionBoundary?: Element | null | (Element | null)[]
  collisionPadding?:
    | number
    | { top?: number; right?: number; bottom?: number; left?: number } // default 0
  arrowPadding?: number // default 0
  sticky?: 'partial' | 'always' // default "partial"
  hideWhenDetached?: boolean
  /**
   * Data attributes:
   *   [data-state]       - "open" | "closed"
   *   [data-side]        - "left" | "right" | "bottom" | "top"
   *   [data-align]       - "start" | "end" | "center"
   *   [data-orientation] - "vertical" | "horizontal"
   *
   * CSS variables:
   *   --radix-dropdown-menu-content-transform-origin	The transform-origin computed from the content and arrow positions/offsets
   *   --radix-dropdown-menu-content-available-width	The remaining width between the trigger and the boundary edge
   *   --radix-dropdown-menu-content-available-height	The remaining height between the trigger and the boundary edge
   *   --radix-dropdown-menu-trigger-width	The width of the trigger
   *   --radix-dropdown-menu-trigger-height	The height of the trigger
   */
  className?: string
  children: ReactNode
}

export const DropdownMenuContent = forwardRef<
  ElementRef<typeof Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  <Portal>
    <Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        // print:hidden — this content is portaled to <body>, so an open menu
        // would otherwise leak into print output (e.g. when a menu item itself
        // triggers window.print()). Overlay menus are never part of a document.
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=closed]:animate-out data-[state=open]:animate-in print:hidden',
        className
      )}
      {...props}
    />
  </Portal>
))
DropdownMenuContent.displayName = Content.displayName
